import React, {
    useState,
    useRef,
    useLayoutEffect,
    useEffect,
    useMemo
} from 'react';
import {
    Autocomplete,
    Alert,
    Box,
    Button,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, themeQuartz } from 'ag-grid-community';
import { myApi } from '../../config/axiosConfig';
import * as XLSX from 'xlsx';

const formato = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const mostrarNumero = value => {
    if (value === null || value === undefined || value === '') {
        return 'N/D';
    }

    const numero = Number(value);
    return Number.isFinite(numero) ? formato.format(numero) : 'N/D';
};

const numeroValido = value => {
    if (
        value === null ||
        value === undefined ||
        String(value).trim() === ''
    ) {
        return null;
    }

    const numero = Number(value);
    return Number.isFinite(numero) ? numero : null;
};

const calcularTotales = registros => 
{
    const obtenerValores = campo =>
        registros
            .map(row => numeroValido(row[campo]))
            .filter(value => value !== null);

    const sumar = campo => {
        const valores = obtenerValores(campo);

        return valores.length
            ? valores.reduce((total, value) => total + value, 0)
            : null;
    };

    const promediar = campo => {
        const valores = obtenerValores(campo);

        return valores.length
            ? valores.reduce((total, value) => total + value, 0)
                / valores.length
            : null;
    };

    const millas = sumar('millas');
    const galones = sumar('galones');

    return {
        id: '__totales__',
        fecha: '',
        operador: `TOTAL (${registros.length} registros)`,
        codigo_operador: '',
        vehiculos_asignados: '',
        millas,
        galones,
        mpg_samsara:
            millas !== null && galones !== null && galones > 0
                ? millas / galones
                : null,
        idle_porcentaje: promediar('idle_porcentaje'),
        cruise_porcentaje: promediar('cruise_porcentaje'),
        horas_conduccion: sumar('horas_conduccion'),
        horas_motor: sumar('horas_motor'),
        calificacion_eficiencia: promediar('calificacion_eficiencia')
    };
};

const temaSamsara = themeQuartz.withParams({
    fontFamily: 'Arial, sans-serif',
    fontSize: 13,
    foregroundColor: '#00152b',
    backgroundColor: '#ffffff',
    headerBackgroundColor: '#f8f8fa',
    headerTextColor: '#00152b',
    headerFontWeight: 600,
    borderColor: '#d9d9df',
    rowBorder: true,
    rowHoverColor: '#eef4ff',
    selectedRowBackgroundColor: '#4b83e5',
    wrapperBorderRadius: 6
});

const columnaNumerica = (
    field,
    headerName,
    width = 110,
    porcentaje = false
) => ({
    field,
    headerName,
    width,
    cellStyle: { textAlign: 'right' },
    valueFormatter: params => {
        const texto = mostrarNumero(params.value);

        if (params.node?.rowPinned && texto === 'N/D') {
            return '';
        }

        return porcentaje && texto !== 'N/D'
            ? `${texto} %`
            : texto;
    }
});

const columns = [
    {
        field: 'fecha',
        headerName: 'Fecha',
        width: 120
    },
    {
        field: 'operador',
        headerName: 'Operador',
        width: 330,
        valueGetter: params => {
            if (!params.data) return '';

            if (params.node?.rowPinned) {
                return params.data.operador;
            }

            return `${params.data.codigo_operador} - ${params.data.operador}`;
        }
    },
    {
        field: 'vehiculos_asignados',
        headerName: 'Vehículos',
        width: 150,
        tooltipField: 'vehiculos_asignados',
        valueFormatter: params =>
            params.node?.rowPinned
                ? ''
                : params.value || 'N/D'
    },
    columnaNumerica('millas', 'Millas'),
    columnaNumerica('galones', 'Galones US'),
    columnaNumerica('mpg_samsara', 'MPG Samsara', 125),
    columnaNumerica('idle_porcentaje', 'Idle', 100, true),
    columnaNumerica(
        'cruise_porcentaje',
        'Cruise Control',
        140,
        true
    ),
    columnaNumerica('horas_conduccion', 'Horas conducción', 155),
    columnaNumerica('horas_motor', 'Horas motor', 125),
    columnaNumerica(
        'calificacion_eficiencia',
        'Calificación',
        125
    )
];

export default function SamsaraRendimiento() {
    // Fecha inicial de prueba: es el día que ya importaste.
    const [fechaInicial, setFechaInicial] = useState('2026-09-01');
    const [fechaFinal, setFechaFinal] = useState('2026-09-01');
    const [operador, setOperador] = useState('');

    const [catalogoOperadores, setCatalogoOperadores] = useState([]);
    const [cargandoOperadores, setCargandoOperadores] = useState(true);
    const [errorOperadores, setErrorOperadores] = useState('');

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [consulta, setConsulta] = useState(null);

    const gridRef = useRef(null);

    const totales = useMemo(
        () => calcularTotales(rows),
        [rows]
    );

    const filasTotales = useMemo(
        () => rows.length ? [totales] : [],
        [rows.length, totales]
    );

    useEffect(() => {
        let vigente = true;

        const cargarOperadores = async () => {
            setCargandoOperadores(true);
            setErrorOperadores('');

            try {
                const { data } = await myApi.get(
                    '/fuel_report/getOperadores',
                    { timeout: 60000 }
                );

                if (!Array.isArray(data?.data)) {
                    throw new Error('El catálogo tiene un formato inesperado.');
                }

                const porCodigo = new Map();

                data.data.forEach(item => {
                    const codigo = String(item.drivercode ?? '').trim();
                    const nombre = String(item.nombre_completo ?? '').trim();

                    if (!codigo) return;

                    porCodigo.set(codigo, {
                        codigo,
                        nombre,
                        etiqueta: nombre
                        ? (
                            nombre.toUpperCase().startsWith(`${codigo.toUpperCase()} -`)
                                ? nombre
                                : `${codigo} - ${nombre}`
                        )
                        : codigo
                    });
                });

                const opciones = [...porCodigo.values()].sort((a, b) =>
                    a.nombre.localeCompare(b.nombre, 'es', {
                        sensitivity: 'base'
                    })
                );

                if (vigente) setCatalogoOperadores(opciones);

            } catch (err) {
                if (vigente) {
                    setErrorOperadores(
                        'No se pudo cargar el catálogo. Recarga la página para reintentar.'
                    );
                }
            } finally {
                if (vigente) setCargandoOperadores(false);
            }
        };

        cargarOperadores();

        return () => {
            vigente = false;
        };
    }, []);

    const buscar = async event => {
        event.preventDefault();

        if (!fechaInicial || !fechaFinal || fechaInicial > fechaFinal) {
            setError('Selecciona un rango de fechas válido.');
            return;
        }

        const filtros = {
            fechaInicial,
            fechaFinal,
            operador: operador.trim()
        };

        setLoading(true);
        setError('');
        setRows([]);
        setConsulta(null);

        try {
            const { data } = await myApi.post(
                '/samsara/rendimiento',
                filtros,
                { timeout: 60000 }
            );

            if (data?.success !== true || !Array.isArray(data.rows)) {
                throw new Error('El servidor devolvió una respuesta inesperada.');
            }

            setRows(data.rows);
            setConsulta(filtros);
        } catch (err) {
            const mensaje = err.response?.data?.error;

            setError(
                typeof mensaje === 'string'
                    ? mensaje
                    : 'No se pudo consultar el reporte. Revisa tu conexión y acceso.'
            );
        } finally {
            setLoading(false);
        }
    };

    const contenedorRef = useRef(null);
    const [altoDisponible, setAltoDisponible] = useState(0);

    useLayoutEffect(() => {
        const elemento = contenedorRef.current;

        if (!elemento) return;

        const medir = () => {
            const top = elemento.getBoundingClientRect().top;

            setAltoDisponible(
                Math.max(0, document.documentElement.clientHeight - top)
            );
        };

        medir();

        const observer = new ResizeObserver(medir);
        observer.observe(document.documentElement);

        // Detecta cambios de altura del menú y otros contenedores superiores.
        let padre = elemento.parentElement;

        while (padre) {
            observer.observe(padre);
            padre = padre.parentElement;
        }

        window.addEventListener('resize', medir);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', medir);
        };
    }, []);

    const descargarExcel = () => 
    {
        const api = gridRef.current?.api;

        if (!api || loading || !consulta || !rows.length) return;

        try {
            const registros = [];

            // Recorre todas las páginas y respeta el orden del grid.
            api.forEachNodeAfterFilterAndSort(node => {
                if (node.data && !node.rowPinned) {
                    registros.push(node.data);
                }
            });

            if (!registros.length) return;

            const resumen = calcularTotales(registros);

            const encabezados = [
                'Fecha',
                'Operador',
                'Vehículos',
                'Millas',
                'Galones US',
                'MPG Samsara',
                'Idle (%)',
                'Cruise Control (%)',
                'Horas conducción',
                'Horas motor',
                'Calificación'
            ];

            const convertirFila = (row, esTotal = false) => [
                row.fecha || '',
                esTotal
                    ? row.operador
                    : `${row.codigo_operador || ''} - ${row.operador || ''}`,
                row.vehiculos_asignados
                    ? String(row.vehiculos_asignados)
                    : '',
                numeroValido(row.millas),
                numeroValido(row.galones),
                numeroValido(row.mpg_samsara),
                numeroValido(row.idle_porcentaje),
                numeroValido(row.cruise_porcentaje),
                numeroValido(row.horas_conduccion),
                numeroValido(row.horas_motor),
                numeroValido(row.calificacion_eficiencia)
            ];

            const datos = [
                encabezados,
                ...registros.map(row => convertirFila(row)),
                convertirFila(resumen, true)
            ];

            const hoja = XLSX.utils.aoa_to_sheet(datos);

            hoja['!cols'] = [
                { wch: 12 },
                { wch: 48 },
                { wch: 20 },
                ...Array.from({ length: 8 }, () => ({ wch: 20 }))
            ];

            // Formato visual; las cantidades siguen siendo números.
            for (let fila = 1; fila < datos.length; fila += 1) {
                for (let columna = 3; columna < encabezados.length; columna += 1) {
                    const direccion = XLSX.utils.encode_cell({
                        r: fila,
                        c: columna
                    });

                    const celda = hoja[direccion];

                    if (celda?.t === 'n') {
                        celda.z = '#,##0.00';
                    }
                }
            }

            // Incluye los datos, dejando el total fuera del autofiltro.
            hoja['!autofilter'] = {
                ref: `A1:K${registros.length + 1}`
            };

            const libro = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(libro, hoja, 'Rendimiento');

            XLSX.writeFile(
                libro,
                `Samsara_Rendimiento_${consulta.fechaInicial}_${consulta.fechaFinal}.xlsx`
            );
        } catch (err) {
            setError('No se pudo generar el archivo Excel.');
        }
    };

    return (
        <Box
            ref={contenedorRef}
            sx={{
                height: altoDisponible ? `${altoDisponible}px` : '100%',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                p: 2,
                boxSizing: 'border-box',
                '& > *': {
                    flexShrink: 0
                }
            }}
        >
            <Typography
                variant="h4"
                sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}
            >
                Samsara
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Rendimiento por operador
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box component="form" onSubmit={buscar}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                    >
                        <TextField
                            label="Fecha inicial"
                            type="date"
                            value={fechaInicial}
                            onChange={e => setFechaInicial(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={loading}
                            required
                        />

                        <TextField
                            label="Fecha final"
                            type="date"
                            value={fechaFinal}
                            onChange={e => setFechaFinal(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            disabled={loading}
                            required
                        />

                        <Autocomplete
                            options={catalogoOperadores}
                            value={
                                catalogoOperadores.find(item => item.codigo === operador)
                                || null
                            }
                            onChange={(_event, seleccionado) => {
                                setOperador(seleccionado?.codigo || '');
                            }}
                            getOptionLabel={opcion => opcion.etiqueta}
                            isOptionEqualToValue={(opcion, seleccionado) =>
                                opcion.codigo === seleccionado.codigo
                            }
                            loading={cargandoOperadores}
                            disabled={loading}
                            loadingText="Cargando operadores…"
                            noOptionsText={
                                errorOperadores
                                    ? 'Catálogo no disponible'
                                    : 'No se encontraron operadores'
                            }
                            openText="Mostrar operadores"
                            closeText="Cerrar listado"
                            clearText="Quitar selección"
                            sx={{
                                width: { xs: '100%', md: 360 },
                                flexShrink: 0
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Operador"
                                    placeholder="Busca por nombre o código"
                                    error={Boolean(errorOperadores)}
                                    helperText={errorOperadores || undefined}
                                />
                            )}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{ minHeight: 48 }}
                        >
                            {loading ? 'Buscando…' : 'Buscar'}
                        </Button>

                        <Button
                            type="button"
                            variant="outlined"
                            onClick={descargarExcel}
                            disabled={loading || !consulta || rows.length === 0}
                            sx={{ minHeight: 48 }}
                        >
                            Descargar Excel
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {consulta && rows.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    No hay registros importados para estos filtros.
                </Alert>
            )}

            <Paper
                variant="outlined"
                sx={{
                    flex: '1 1 0',
                    minHeight: 0,
                    minWidth: 0,
                    width: '100%',
                    overflow: 'hidden'
                }}
            >
                <AgGridReact
                    modules={[AllCommunityModule]}
                    theme={temaSamsara}
                    rowData={rows}
                    columnDefs={columns}
                    loading={loading}
                    getRowId={params => String(params.data.id)}
                    defaultColDef={{
                        sortable: true,
                        resizable: true,
                        minWidth: 85
                    }}
                    rowHeight={42}
                    headerHeight={48}
                    pagination={true}
                    paginationPageSize={100}
                    paginationPageSizeSelector={[25, 50, 100]}
                    enableCellTextSelection={true}
                    overlayNoRowsTemplate={
                        consulta
                            ? '<span>Sin registros</span>'
                            : '<span>Selecciona los filtros y presiona Buscar</span>'
                    }
                    ref={gridRef}
                    pinnedBottomRowData={filasTotales}
                    getRowStyle={params =>
                        params.node.rowPinned
                            ? {
                                fontWeight: 700,
                                backgroundColor: '#eaf2ff'
                            }
                            : undefined
                    }
                />
            </Paper>
        </Box>
    );
}